import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { Loader2 } from "lucide-react";

interface GuestGuardProps {
  children: React.ReactNode;
}

export function GuestGuard({ children }: GuestGuardProps) {
  const { isAuthenticated, isLoading, loadUser, user } = useAuthStore();
  const [searchParams] = useSearchParams();

  /**
   * Falha de SSO tem de ser lida, mesmo com sessão viva.
   *
   * O callback do Entra recusa para `/login?sso_error=<code>` (S-073 decisão 3).
   * Com uma sessão myGROW de pé — aba antiga, link direto para
   * `/api/auth/entra/login`, ou tentativa de entrar com uma segunda conta —, o
   * desvio daqui acontecia antes de a `Login` montar, e o `sso_error` morria sem
   * ninguém ler: a pessoa caía na lista de projetos sem explicação. É o mesmo
   * beco que o redirect existe para fechar, só um passo depois.
   *
   * **Lido uma vez, no mount**, e não a cada render: a `Login` apaga o param da
   * URL assim que o lê (`Login.tsx:108`), e reavaliar traria o desvio de volta no
   * render seguinte — a mensagem apareceria e sumiria. Mesmo padrão do
   * `useState(() => searchParams.get("sso_error"))` de lá, pela mesma razão.
   *
   * Vale para `/` também, que compartilha este guarda: `sso_error` só é anexado a
   * `/login`, então na prática a landing page não recebe. Se alguém digitar,
   * ganha a landing page em vez do desvio — que é pública, e não vaza nada.
   */
  const [ssoFailed] = useState(() => searchParams.has("sso_error"));

  useEffect(() => {
    if (isLoading && !user) {
      loadUser();
    }
  }, [isLoading, user, loadUser]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a1628]">
        <Loader2 className="h-8 w-8 animate-spin text-white/50" />
      </div>
    );
  }

  if (isAuthenticated && !ssoFailed) {
    return <Navigate to="/projects" replace />;
  }

  return <>{children}</>;
}
